import { useQuery } from '@apollo/client';
import {
  Currencies,
  FeatureFlagTypes,
  Frequency,
  WithdrawalMode,
  WithdrawalReason,
} from '@generated/graphql';
import type { BankAccount } from '@interfaces/bankAccount';
import { AccountTypes, CurrencyCodes } from '@onevesthq/ov-enums';
import { ExternalLink } from '@ovComponents/1-primative/link/externalLink';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { BankAccountSelectField } from '@ovComponents/3-pattern/bankAccountSelectField/bankAccountSelect';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { TransferContext } from '@ovComponents/4-module/withdrawWorkflow/withdrawWorkflowContext';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import {
  INCOME_FUND_ACCOUNTS,
  LOCKED_IN_ACCOUNTS,
} from '@ovComponents/4-module/workflowCompletion/constants/accounts';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import {
  type ValidateTransferResponse,
  validateTransfer,
} from '@ovComponents/accountRestrictions';
import { TransferErrorBanner } from '@ovComponents/transferErrorBanner';
import { UserContext } from '@providers/userContextProvider';
import {
  addBusinessDays,
  isBusinessDay,
  isBusinessDaySync,
  preloadBusinessDaysHolidays,
} from '@utils/businessDays';
import { DEFAULT_SUPPORT_URL } from '@utils/constants';
import { updateContextTransfer } from '@utils/updateWorkflowContext';
import dayjs from 'dayjs';
import {
  Alert,
  Grid,
  Stack,
  Typography,
  useTheme,
  useToast,
} from 'hephaestus/web';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { usePermission } from '../../../../../hooks/usePermission';
import { getAccountConfig } from '../editAccount/accountConfig';
import { FETCH_USER_LOCALIZATION } from '../queries';
import { invalidFields } from '../utils';
import type { FederalTaxConfig, StateTaxConfig } from '../utils/taxConfig';
import { useBlockingToast } from './useBlockingToast';
import { useWithdrawalFeatureActive } from './useWithdrawalFeatureActive';
import { useWithdrawalMode } from './useWithdrawalMode';
import { WithdrawalAmountFields } from './WithdrawalAmountFields';
import { WithdrawalModeSelect } from './WithdrawalModeSelect';
import WithdrawalBreakdown, {
  type MultiCurrencyAmount,
} from './withdrawalBreakdown';
import {
  isFullTransfer,
  toDefaultWithdrawalMode,
  toWithdrawalValidationOptions,
} from './withdrawalMode.utils';
import { withdrawSubAccountFilter } from './withdrawSubAccountFilter';

const RRSP_ACCOUNTS = [AccountTypes.RRSP, AccountTypes.RRSP_SPOUSAL];
const RESP_ACCOUNTS = [
  AccountTypes.RESP,
  AccountTypes.RESP_ADULT,
  AccountTypes.RESP_FAMILY,
  AccountTypes.RESP_FAMILY_JOINT,
  AccountTypes.RESP_SINGLE,
  AccountTypes.RESP_SINGLE_JOINT,
];

export const getAvailableFundsToWithdrawCents = (
  applicableData: any,
  enableMultiCurrencyWithdrawalTransfers: boolean,
): number => {
  const { statistics } = applicableData?.subAccount ?? {};
  const { account } = applicableData?.subAccount ?? {};

  const isMultiCurrencyAccount =
    enableMultiCurrencyWithdrawalTransfers &&
    account?.availableCurrencies?.length > 1;

  const currency =
    applicableData?.bankAccount?.currency ??
    account?.baseCurrency ??
    CurrencyCodes.CAD;

  return isMultiCurrencyAccount
    ? (statistics?.originalCurrencies?.find(
        (item: MultiCurrencyAmount) => item.currency === currency,
      )?.availableFundsToWithdrawCents ?? 0)
    : (statistics?.availableFundsToWithdrawCents ?? 0);
};

export const WithdrawForm = ({
  options,
  onNext,
  dummyWithdraw,
  setDummyWithdraw,
  workflowCompletion,
}: {
  options: any;
  onNext: any;
  dummyWithdraw?: any;
  setDummyWithdraw?: any;
  workflowCompletion?: { objectType?: string; objectId?: string };
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions', 'transfer']);
  const theme = useTheme();
  const { enqueueToast } = useToast();
  const canWriteOverrideTransferRestrictions = usePermission(
    'write:override_transfer_restrictions',
  );
  const canWriteTransferOboClients = usePermission(
    'write:transfer_obo_clients',
  );
  const canWriteTransferBasic = usePermission('write:transfer_basic');
  const { userId: paramsUserId } = useParams();
  const { transferData, setTransferData } = useContext(TransferContext);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { activeEntity, userContext, activeOrganization } =
    useContext(UserContext);
  const enableMultiCurrencyWithdrawalTransfers = useOrganizationFeatureFlag(
    FeatureFlagTypes.MultiCurrencyWithdrawTransfers,
  );
  const usTransferFlowEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.UsTransferFlow,
  );

  const [starting, setStarting] = useState<'now' | 'futureDate'>('now');
  const [focused, setFocused] = useState<string[]>([]);
  const [dateHasChanged, setDateHasChanged] = useState(false);
  const [stateTaxError, setStateTaxError] = useState<boolean>(false);
  const [isWithdrawAll, setIsWithdrawAll] = useState<boolean>(false);

  const [federalTaxConfig, setFederalTaxConfig] =
    useState<FederalTaxConfig | null>();
  const [stateTaxConfig, setStateTaxConfig] = useState<StateTaxConfig | null>();
  // Prefer sub-account owner over activeEntity when the route has no :userId (household nav).
  const userIdFromSubAccount =
    transferData?.subAccount?.account?.user?.id ||
    workflowData.subAccount?.account?.user?.id;
  const userId = paramsUserId ?? userIdFromSubAccount ?? activeEntity?.id;

  const { data: localizationData } = useQuery(FETCH_USER_LOCALIZATION, {
    variables: { userId },
    skip: !userId,
  });

  const defaultScheduleOption =
    options?.editDefaultScheduleFrequency && options?.defaultScheduleOption
      ? options?.defaultScheduleOption
      : undefined;
  const storedApplicableData =
    dummyWithdraw ??
    (Object.keys(transferData).length !== 0 ? transferData : workflowData);
  const applicableData = {
    ...storedApplicableData,
    frequency: storedApplicableData.frequency ?? defaultScheduleOption,
  };
  // Skip fetchBankAccounts until :userId or sub-account when we would otherwise use only activeEntity.
  const bankAccountsQueryUserId = paramsUserId
    ? (userId ?? '')
    : applicableData?.subAccount?.id
      ? (userId ?? '')
      : '';
  const setApplicationContext =
    setDummyWithdraw ??
    (Object.keys(transferData).length !== 0 ? setTransferData : undefined);
  const setWorkflowCompletionContext =
    Object.keys(transferData).length === 0 ? setWorkflowData : undefined;

  const hyperLinkColor = theme.color.negativeOutline;
  const supportUrl =
    translateBackendString(userContext.organization?.translatedSupportUrl) ||
    DEFAULT_SUPPORT_URL;
  const hasOverrideTransferPermission = canWriteOverrideTransferRestrictions;

  const IS_RRSP = RRSP_ACCOUNTS.includes(
    applicableData.subAccount?.account?.type,
  );
  const IS_RRSP_AND_LLP_OR_HBP_SELECTED =
    IS_RRSP &&
    [
      WithdrawalReason.HomeBuyersPlan,
      WithdrawalReason.LifelongLearningPlan,
    ].includes(applicableData.withdrawalReason);

  const IS_RESP = RESP_ACCOUNTS.includes(
    applicableData.subAccount?.account?.type,
  );
  const IS_LOCKED_IN = LOCKED_IN_ACCOUNTS.includes(
    applicableData.subAccount?.account?.type,
  );
  const IS_INCOME_FUND_ACCOUNT = INCOME_FUND_ACCOUNTS.includes(
    applicableData.subAccount?.account?.type,
  );
  const IS_FHSA =
    AccountTypes.FHSA === applicableData.subAccount?.account?.type;
  const IS_USA_RT_IRA =
    AccountTypes.USA_RT_IRA === applicableData.subAccount?.account?.type;
  const disableForm =
    (!hasOverrideTransferPermission && IS_RRSP_AND_LLP_OR_HBP_SELECTED) ||
    IS_RESP ||
    IS_LOCKED_IN ||
    IS_INCOME_FUND_ACCOUNT ||
    IS_FHSA ||
    stateTaxError;
  const transferDate = applicableData?.scheduledDate;
  const delayDays = options?.transferDelay?.number;
  const transferCountry =
    applicableData?.bankAccount?.bankAccountTransitPhysicalAddress?.country ||
    localizationData?.fetchUser?.user?.physicalAddress?.country ||
    localizationData?.fetchUser?.user?.countryOfTaxResidence ||
    activeOrganization.applicableLocalization.countries[0];

  const confirmTransferDelay = useCallback(
    async ({
      scheduledDate,
      days,
      country,
    }: {
      scheduledDate: Date | string | undefined;
      days: number | undefined;
      country: string;
    }) => {
      const today = new Date();
      const minTransferDate = await addBusinessDays(today, days ?? 0, country);

      return {
        dateIsAfter:
          dayjs(scheduledDate).isSame(minTransferDate, 'day') ||
          dayjs(scheduledDate).isAfter(minTransferDate),
        minTransferDate: dayjs(minTransferDate).format('MM/DD/YYYY'),
      };
    },
    [],
  );

  const [transferDelayCheck, setTransferDelayCheck] = useState<{
    dateIsAfter: boolean;
    minTransferDate: string;
  } | null>(null);
  const [scheduledDateIsBusinessDay, setScheduledDateIsBusinessDay] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    void preloadBusinessDaysHolidays();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [delayResult, isBd] = await Promise.all([
        confirmTransferDelay({
          scheduledDate: transferDate,
          days: delayDays,
          country: transferCountry,
        }),
        isBusinessDay({
          date: dayjs(transferDate).utc().toDate(),
          country: transferCountry,
        }),
      ]);
      if (!cancelled) {
        setTransferDelayCheck(delayResult);
        setScheduledDateIsBusinessDay(isBd);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transferDate, delayDays, transferCountry, confirmTransferDelay]);

  const isScheduled = starting !== 'now';
  const invalidTransferDelayCheck =
    isScheduled &&
    options?.transferDelay?.enabled &&
    (!transferDelayCheck ||
      !transferDelayCheck.dateIsAfter ||
      scheduledDateIsBusinessDay === false ||
      scheduledDateIsBusinessDay === null);

  const scheduledDateError =
    (!transferDate &&
      options?.scheduledDate?.required !== FieldRequiredType.NOT_REQUIRED) ||
    scheduledDateIsBusinessDay === false ||
    (options?.transferDelay?.enabled &&
      !!transferDelayCheck &&
      !transferDelayCheck.dateIsAfter);

  const scheduledDateErrorMessage = () => {
    if (
      options?.transferDelay?.enabled &&
      transferDelayCheck &&
      !transferDelayCheck.dateIsAfter
    ) {
      const date = dayjs(transferDelayCheck.minTransferDate).format(
        'MM/DD/YYYY',
      );
      return t('bankAccountTransferCreation.transferDelayError', { date });
    }
    return t('bankAccountTransferCreation.enterAValidDate');
  };

  const isInitiatedByRepresentative = () =>
    canWriteTransferOboClients && !canWriteTransferBasic;

  // Use ref to store latest applicableData to avoid recreating handleValue on every change
  const applicableDataRef = useRef(applicableData);
  applicableDataRef.current = applicableData;

  const isWithdrawalModeOptionEnabled = Boolean(
    options?.enableWithdrawalMode && options?.amountCents?.enabled,
  );
  const isWithdrawalFeatureActive = useWithdrawalFeatureActive({
    optionEnabled: isWithdrawalModeOptionEnabled,
    storedMode: applicableData.withdrawalMode,
    netAmountCents: applicableData.netAmountCents,
  });
  const isWithdrawalFeatureActiveRef = useRef(isWithdrawalFeatureActive);
  isWithdrawalFeatureActiveRef.current = isWithdrawalFeatureActive;

  const handleValue = useCallback(
    (value: Record<string, any>) => {
      // Net needs the bare patch: the context setter merges it into the latest state, while the
      // render-time snapshot would let a second call in the same commit restore keys the first one
      // just changed. Gross keeps the snapshot merge so the legacy flow behaves as it always did.
      return updateContextTransfer({
        value: isWithdrawalFeatureActiveRef.current
          ? value
          : { ...applicableDataRef.current, ...value },
        existingContext: applicableDataRef.current,
        setApplicationContext,
        setWorkflowCompletionContext,
      });
    },
    [setApplicationContext, setWorkflowCompletionContext],
  );

  const availableCents = getAvailableFundsToWithdrawCents(
    applicableData,
    enableMultiCurrencyWithdrawalTransfers,
  );
  const withdrawalMode = useWithdrawalMode({
    optionEnabled: isWithdrawalModeOptionEnabled,
    accountId: applicableData.subAccount?.account?.id,
    frequency: applicableData.frequency,
    scheduledDate: applicableData.scheduledDate,
    isFutureDated: starting === 'futureDate',
    storedMode: applicableData.withdrawalMode,
    defaultMode: toDefaultWithdrawalMode(options?.withdrawalMode),
    netAmountCents: applicableData.netAmountCents,
    netAmountLabel: options?.netAmountLabel,
    storedNetAmountLabel: applicableData.netAmountLabel,
    amountCents: applicableData.amountCents,
    isWithdrawAll,
    isMultiCurrency:
      enableMultiCurrencyWithdrawalTransfers &&
      applicableData?.subAccount?.account?.availableCurrencies?.length > 1,
    availableCents,
    handleValue,
  });
  const isNetWithdrawal = withdrawalMode.mode === WithdrawalMode.Net;

  useBlockingToast({
    isActive: withdrawalMode.exceedsAvailable,
    message: t('transfer:withdrawal.withdrawalBreakDown.netExceedsAvailable'),
    variant: 'warning',
  });

  const handleSubAccountSelect = useCallback(
    (subAccount: any) => {
      handleValue({
        subAccount,
        federalTaxPercentage: undefined,
        stateTaxPercentage: undefined,
        bankAccount: undefined,
        ...(isNetWithdrawal && { amountCents: undefined }),
      });
    },
    [handleValue, isNetWithdrawal],
  );

  const shouldPersistDefaultFrequency =
    isWithdrawalFeatureActive &&
    !storedApplicableData.frequency &&
    Boolean(defaultScheduleOption);

  useEffect(() => {
    if (
      applicableData?.frequency &&
      applicableData?.frequency !== Frequency.OneTime
    ) {
      handleValue({
        frequency: applicableData?.frequency ?? defaultScheduleOption,
      });
    }
    if (applicableData.frequency === Frequency.OneTime && starting === 'now') {
      handleValue({ scheduledDate: undefined });
    }
    // The bare patch of the Net-capable form does not carry the defaulted frequency along with other writes.
    if (shouldPersistDefaultFrequency) {
      handleValue({ frequency: defaultScheduleOption });
    }
  }, [
    applicableData?.frequency,
    shouldPersistDefaultFrequency,
    starting,
    defaultScheduleOption,
  ]);

  const submit = async () => {
    const fields = invalidFields(
      {
        ...toWithdrawalValidationOptions(options, isNetWithdrawal),
        scheduledDate: {
          ...options.scheduledDate,
          requiredIf: (data: any) =>
            data?.frequency !== Frequency.OneTime || starting === 'futureDate',
        },
        schedule: {
          ...options.schedule,
          requiredIf: (data: any) => data?.frequency !== Frequency.OneTime,
        },
      },
      applicableData,
    );
    setFocused(fields);

    const transferValidation: ValidateTransferResponse = validateTransfer({
      accountTo: applicableData?.subAccount?.account?.type,
      transferType: 'electronicFundsTransferOut',
      withdrawalReason: applicableData?.withdrawalReason,
      canOverrideTransferRestrictions: hasOverrideTransferPermission,
    });
    if (!transferValidation.isValid) {
      enqueueToast(transferValidation.message, { variant: 'error' });
      return;
    }

    if (invalidTransferDelayCheck) return;

    if (fields.length === 0) {
      await onNext();
    }
  };

  useEffect(() => {
    // A Net entry never becomes withdraw-all; the mode hook blocks Continue instead.
    if (isNetWithdrawal) return;

    const availableFundsToWithdrawCents = getAvailableFundsToWithdrawCents(
      applicableData,
      enableMultiCurrencyWithdrawalTransfers,
    );

    if (applicableData.amountCents > availableFundsToWithdrawCents) {
      handleValue({ amountCents: availableFundsToWithdrawCents });
    }

    const isFullTransferResult = isFullTransfer(
      applicableData.amountCents ?? 0,
      availableFundsToWithdrawCents,
    );

    setIsWithdrawAll(isFullTransferResult);
  }, [
    applicableData.amountCents,
    applicableData.bankAccount?.currency,
    applicableData.subAccount?.id,
    enableMultiCurrencyWithdrawalTransfers,
    isNetWithdrawal,
    handleValue,
  ]);

  const accountConfig = getAccountConfig(
    applicableData?.subAccount?.account,
    options,
    t,
  );

  const jurisdiction =
    applicableData?.subAccount?.account?.user?.physicalAddress?.jurisdiction?.replace(
      'US_',
      '',
    );

  const stateTaxPercentage = applicableData?.stateTaxPercentage ?? undefined;
  const federalTaxPercentage =
    applicableData?.federalTaxPercentage ?? undefined;
  const federalTaxWithheld =
    (applicableData?.amountCents ?? 0) * (federalTaxPercentage / 100);
  const stateTaxWithheld =
    (applicableData?.amountCents ?? 0) * (stateTaxPercentage / 100);

  useEffect(() => {
    setStateTaxError(false);
    const taxConfig = accountConfig?.taxConfig;

    if (!usTransferFlowEnabled || !taxConfig) {
      setFederalTaxConfig(undefined);
      setStateTaxConfig(undefined);

      handleValue({
        federalTaxPercentage: undefined,
        stateTaxPercentage: undefined,
      });

      return;
    }

    const newFederalTaxConfig = taxConfig.federalTaxConfig;
    const newStateTaxConfig = taxConfig.stateTaxConfig[jurisdiction];

    setFederalTaxConfig(newFederalTaxConfig);
    setStateTaxConfig(newStateTaxConfig);

    // Set default values for tax percentages when changing account. Maintain existing values if they exist while navigating back from the next step.
    handleValue({
      federalTaxPercentage:
        federalTaxPercentage ?? newFederalTaxConfig?.defaultTaxWithholding,
      stateTaxPercentage:
        stateTaxPercentage ??
        newStateTaxConfig?.defaultTaxWithholding?.(
          newFederalTaxConfig.defaultTaxWithholding,
          isWithdrawAll,
        ),
    });
  }, [applicableData?.subAccount?.account?.id]);

  useEffect(() => {
    if (stateTaxConfig && stateTaxConfig.isValidTaxWithholding) {
      if (
        stateTaxConfig.isValidTaxWithholding(
          applicableData?.stateTaxPercentage,
          applicableData?.federalTaxPercentage,
          isWithdrawAll,
        )
      ) {
        setStateTaxError(false);
      } else {
        setStateTaxError(true);
      }
    }
  }, [isWithdrawAll]);

  const PREMATURE_WITHDRAWAL_ACCOUNT_TYPES = [
    AccountTypes.USA_RO_IRA,
    AccountTypes.USA_IRA,
    AccountTypes.USA_RT_IRA,
  ];
  const ownerOlderThanPrematureCutoff =
    dayjs()
      .utc()
      .diff(
        applicableData?.subAccount?.account?.user?.dateOfBirth,
        'years',
        true,
      ) >= 59.5;
  const isPrematureWithdrawal =
    usTransferFlowEnabled &&
    PREMATURE_WITHDRAWAL_ACCOUNT_TYPES.includes(
      applicableData?.subAccount?.account?.type,
    ) &&
    !ownerOlderThanPrematureCutoff;

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        disabled:
          disableForm ||
          withdrawalMode.grossPending ||
          withdrawalMode.exceedsAvailable,
        dataTestId: 'withdraw-form-continue-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackendString(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        {translateBackendString(options?.subtitle)}
      </Typography>

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {options?.subAccount?.enabled && (
            <SubAccountSelect
              dataTestId='withdraw-form-subaccount-select'
              label={translateBackendString(options?.subAccount?.label)}
              omitAny
              onSubAccountSelect={handleSubAccountSelect}
              selectedSubAccount={applicableData.subAccount}
              filter={withdrawSubAccountFilter({
                contextObjectType: applicableData?.objectType,
                contextObjectId: applicableData?.objectId,
                completionObjectType: workflowCompletion?.objectType,
                completionObjectId: workflowCompletion?.objectId,
                userId,
              })}
              filterNotAvailableWithdraw
              sx={{ mt: 2, mb: 2 }}
              onBlur={() => setFocused([...focused, 'subAccount'])}
              error={
                !applicableData.subAccount?.id &&
                focused.includes('subAccount') &&
                options?.subAccount?.required !== FieldRequiredType.NOT_REQUIRED
              }
              showCurrencyAvatar={enableMultiCurrencyWithdrawalTransfers}
            />
          )}
          {options?.bankAccount?.enabled && (
            <BankAccountSelectField
              bankAccount={applicableData.bankAccount}
              options={options}
              setBankAccount={(bankAccount: BankAccount | null) =>
                handleValue({ bankAccount })
              }
              userId={bankAccountsQueryUserId}
              disabled={!bankAccountsQueryUserId}
              account={applicableData?.subAccount?.account}
              key={`bank-account-select-${applicableData?.subAccount?.id}`}
              sx={{ mb: 2 }}
              onBlur={() => setFocused([...focused, 'bankAccount'])}
              error={
                !applicableData.bankAccount?.id &&
                focused.includes('bankAccount') &&
                options?.bankAccount?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
              transactionType='WITHDRAWAL'
              showSelectedIcon={enableMultiCurrencyWithdrawalTransfers}
              isMultiCurrencyAccount={
                enableMultiCurrencyWithdrawalTransfers &&
                applicableData?.subAccount?.account?.availableCurrencies
                  ?.length > 1
              }
              multiCurrencyAmounts={
                applicableData?.subAccount?.statistics?.originalCurrencies ?? []
              }
            />
          )}
          {withdrawalMode.isSelectable && (
            <WithdrawalModeSelect
              disclaimer={options?.withdrawalModeDisclaimer}
              mode={withdrawalMode.mode}
              onModeChange={withdrawalMode.selectMode}
            />
          )}
          <WithdrawalAmountFields
            amountOptions={options?.amountCents}
            netAmountLabel={options?.netAmountLabel}
            mode={withdrawalMode.mode}
            amountCents={applicableData.amountCents}
            netAmountCents={applicableData.netAmountCents}
            currency={
              enableMultiCurrencyWithdrawalTransfers &&
              (applicableData?.bankAccount?.currency ||
                applicableData?.subAccount?.account?.baseCurrency ||
                activeOrganization?.productShelfCurrencies?.[0] ||
                Currencies.Cad)
            }
            focused={focused}
            onFieldBlur={(field) => setFocused([...focused, field])}
            onAmountChange={(cents) => handleValue({ amountCents: cents })}
            onNetAmountChange={withdrawalMode.setNetAmountCents}
          />
          {options?.schedule?.enabled && (
            <SelectField
              label={t('withdrawReview.frequency')}
              testId='withdrawl-form-dropdown-frequency'
              onChange={(e: any) =>
                handleValue({
                  frequency: e.target.value,
                  schedule: e.target.value,
                })
              }
              value={applicableData?.frequency ?? defaultScheduleOption ?? ''}
              fullWidth
              sx={{ mb: 2 }}
              onBlur={() => setFocused([...focused, 'schedule'])}
              error={
                !applicableData.frequency &&
                focused.includes('schedule') &&
                options?.schedule?.required !== FieldRequiredType.NOT_REQUIRED
              }
            >
              <MenuItem
                style={{ display: 'none' }}
                value=''
                data-testid='empty-frequency'
              ></MenuItem>
              <MenuItem
                value={Frequency.OneTime}
                data-testid='frequency-onetime'
              >
                {t('transfer:frequencyOption.ONE_TIME')}
              </MenuItem>
              <MenuItem value='WEEKLY' data-testid='frequency-weekly'>
                {t('transfer:frequencyOption.WEEKLY')}
              </MenuItem>
              <MenuItem value='BI_WEEKLY' data-testid='frequency-biweekly'>
                {t('transfer:frequencyOption.BI_WEEKLY')}
              </MenuItem>
              <MenuItem value='MONTHLY' data-testid='frequency-monthly'>
                {t('transfer:frequencyOption.MONTHLY')}
              </MenuItem>
              <MenuItem
                value='SEMI_MONTHLY'
                data-testid='frequency-semi-monthly'
              >
                {t('transfer:frequencyOption.SEMI_MONTHLY')}
              </MenuItem>
              <MenuItem value='QUARTERLY' data-testid='frequency-quarterly'>
                {t('transfer:frequencyOption.QUARTERLY')}
              </MenuItem>
            </SelectField>
          )}
          {options?.schedule?.enabled &&
            [
              'WEEKLY',
              'BI_WEEKLY',
              'MONTHLY',
              'SEMI_MONTHLY',
              'QUARTERLY',
            ].includes(applicableData.frequency) && (
              <DateField
                onChange={(date: any) =>
                  handleValue({
                    scheduledDate: dayjs(date?.toString()).format('YYYY-MM-DD'),
                  })
                }
                onAccept={(date: any) => {
                  setDateHasChanged(true);
                  if (dayjs(date).isBefore(dayjs(), 'day')) {
                    enqueueToast(
                      t('bankAccountTransferCreation.enterAValidDate'),
                      { variant: 'error' },
                    );
                    handleValue({ scheduledDate: undefined });
                  } else if (
                    options?.transferDelay?.enabled &&
                    !transferDelayCheck?.dateIsAfter
                  ) {
                    handleValue({ scheduledDate: undefined });
                  } else {
                    handleValue({
                      scheduledDate: dayjs(date?.toString()).format(
                        'YYYY-MM-DD',
                      ),
                    });
                  }
                }}
                label={t('transfer:withdrawal.starting')}
                minDate={
                  options?.transferDelay?.enabled
                    ? (transferDelayCheck?.minTransferDate ??
                      dayjs().add(1, 'day'))
                    : dayjs().add(1, 'day')
                }
                shouldDisableDate={(date: any) =>
                  !isBusinessDaySync({
                    date: dayjs(date).utc().toDate(),
                    country: transferCountry,
                  })
                }
                fullWidth
                sx={{ mb: 2 }}
                value={applicableData.scheduledDate ?? defaultScheduleOption}
                error={
                  scheduledDateError &&
                  (dateHasChanged || focused.includes('scheduledDate'))
                }
                errorText={scheduledDateErrorMessage()}
              />
            )}
          {options?.schedule?.enabled &&
            applicableData?.frequency === Frequency.OneTime && (
              <RadioGroup
                testId='withdraw-form-starting'
                label={t('components:transferModal.starting')}
                value={starting}
                onChange={(e: any) => setStarting(e.target.value)}
              >
                <Stack direction={'row'}>
                  {!isInitiatedByRepresentative() && (
                    <Radio
                      value='now'
                      label={t('components:transferModal.startingOptions.now')}
                    />
                  )}
                  <Radio
                    value='futureDate'
                    label={t(
                      'components:transferModal.startingOptions.futureDate',
                    )}
                  />
                </Stack>
              </RadioGroup>
            )}
          {starting === 'futureDate' &&
            applicableData?.frequency === Frequency.OneTime && (
              <DateField
                sx={{ mb: 2 }}
                dataTestId='scheduled-date'
                onChange={(date: any) =>
                  handleValue({
                    scheduledDate: dayjs(date?.toString()).format('YYYY-MM-DD'),
                  })
                }
                onAccept={(date: any) => {
                  setDateHasChanged(true);
                  if (dayjs(date).isBefore(dayjs(), 'day')) {
                    enqueueToast(
                      t('bankAccountTransferCreation.enterAValidDate'),
                      { variant: 'error' },
                    );
                    handleValue({ scheduledDate: undefined });
                  } else if (
                    options?.transferDelay?.enabled &&
                    !transferDelayCheck?.dateIsAfter
                  ) {
                    handleValue({ scheduledDate: undefined });
                  } else {
                    handleValue({
                      scheduledDate: dayjs(date?.toString()).format(
                        'YYYY-MM-DD',
                      ),
                    });
                  }
                }}
                label={translateBackendString(options?.scheduledDate?.label)}
                minDate={
                  options?.transferDelay?.enabled
                    ? (transferDelayCheck?.minTransferDate ??
                      dayjs().add(1, 'day'))
                    : dayjs().add(1, 'day')
                }
                shouldDisableDate={(date: any) =>
                  !isBusinessDaySync({
                    date: dayjs(date).utc().toDate(),
                    country: transferCountry,
                  })
                }
                fullWidth
                value={applicableData?.scheduledDate}
                error={
                  scheduledDateError &&
                  (dateHasChanged || focused.includes('scheduledDate'))
                }
                errorText={scheduledDateErrorMessage()}
              />
            )}
          {options?.transferDelay?.enabled &&
            ((applicableData.frequency === Frequency.OneTime &&
              starting !== 'now') ||
              (applicableData.frequency !== Frequency.OneTime &&
                applicableData.frequency !== undefined)) && (
              <Alert
                sx={{ mb: 2 }}
                severity='info'
                data-testid='transfer-delay-alert'
              >
                {translateBackendString(options?.transferDelayDescription)}
              </Alert>
            )}
          {options?.withdrawalReason?.enabled && (
            <SelectField
              testId='reason-dropdown'
              label={translateBackendString(options?.withdrawalReason?.label)}
              onChange={(e: any) =>
                handleValue({ withdrawalReason: e.target.value })
              }
              value={applicableData?.withdrawalReason ?? ''}
              fullWidth
              sx={{ mb: 2 }}
              onBlur={() => setFocused([...focused, 'withdrawalReason'])}
              error={
                !applicableData.withdrawalReason &&
                focused.includes('withdrawalReason') &&
                options?.withdrawalReason?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            >
              <MenuItem
                style={{ display: 'none' }}
                value=''
                data-testid='empty-reason'
              ></MenuItem>
              <MenuItem
                value={WithdrawalReason.CompletedMyGoal}
                data-testid='completed-my-goal'
              >
                {t(
                  'transfer:withdrawal.withdrawalReasonOptions.COMPLETED_MY_GOAL',
                )}
              </MenuItem>
              <MenuItem
                value={WithdrawalReason.DissatisfiedWithOnevest}
                data-testid='dissatisified-with-onevest'
              >
                {t(
                  'transfer:withdrawal.withdrawalReasonOptions.DISSATISFIED_WITH_ONEVEST',
                )}
              </MenuItem>
              <MenuItem
                value={WithdrawalReason.InvestingIsNotForMe}
                data-testid='investing-not-for-me'
              >
                {t(
                  'transfer:withdrawal.withdrawalReasonOptions.INVESTING_IS_NOT_FOR_ME',
                )}
              </MenuItem>
              <MenuItem
                value={WithdrawalReason.PoorReturns}
                data-testid='poor-returns'
              >
                {t('transfer:withdrawal.withdrawalReasonOptions.POOR_RETURNS')}
              </MenuItem>
              {RRSP_ACCOUNTS.includes(
                applicableData.subAccount?.account?.type,
              ) && (
                <MenuItem value={WithdrawalReason.HomeBuyersPlan}>
                  {t(
                    'transfer:withdrawal.withdrawalReasonOptions.HOME_BUYERS_PLAN',
                  )}
                </MenuItem>
              )}
              {RRSP_ACCOUNTS.includes(
                applicableData.subAccount?.account?.type,
              ) && (
                <MenuItem value={WithdrawalReason.LifelongLearningPlan}>
                  {t(
                    'transfer:withdrawal.withdrawalReasonOptions.LIFELONG_LEARNING_PLAN',
                  )}
                </MenuItem>
              )}
              <MenuItem value={WithdrawalReason.Other} data-testid='others'>
                {t('transfer:withdrawal.withdrawalReasonOptions.OTHER')}
              </MenuItem>
            </SelectField>
          )}
          {usTransferFlowEnabled && federalTaxConfig && stateTaxConfig && (
            <NumberField
              label={t('components:transferModal.federalTaxTitle')}
              number={(applicableData?.federalTaxPercentage ?? 0).toString()}
              disabled={!federalTaxConfig.canChangeTaxWithholding}
              decimalPlaces={2}
              setNumber={(e: any) => {
                const newFederalTaxPercentage = e ? parseFloat(e) : 0;
                const newStateTaxPercentage =
                  stateTaxConfig.defaultTaxWithholding(
                    newFederalTaxPercentage,
                    isWithdrawAll,
                  );

                handleValue({
                  stateTaxPercentage: newStateTaxPercentage,
                  federalTaxPercentage: newFederalTaxPercentage,
                });

                if (!stateTaxConfig.isValidTaxWithholding) return;

                const isValidTaxWithHolding =
                  stateTaxConfig.isValidTaxWithholding(
                    newStateTaxPercentage,
                    newFederalTaxPercentage,
                    isWithdrawAll,
                  );
                setStateTaxError(!isValidTaxWithHolding);
              }}
              max={100}
              min={0}
              testId='federal-tax'
            />
          )}
          {usTransferFlowEnabled && federalTaxConfig && stateTaxConfig && (
            <NumberField
              label={t('components:transferModal.stateTaxTitle')}
              number={(applicableData?.stateTaxPercentage ?? 0).toString()}
              disabled={
                !stateTaxConfig.canChangeTaxWithholding(
                  applicableData?.federalTaxPercentage,
                )
              }
              decimalPlaces={2}
              sx={{ mt: 2 }}
              setNumber={(e: any) => {
                const newStateTaxPercentage = e ? parseFloat(e) : 0;
                handleValue({ stateTaxPercentage: newStateTaxPercentage });

                if (!stateTaxConfig.isValidTaxWithholding) return;

                const isValidTaxWithHolding =
                  stateTaxConfig.isValidTaxWithholding(
                    newStateTaxPercentage,
                    applicableData?.federalTaxPercentage,
                    isWithdrawAll,
                  );
                setStateTaxError(!isValidTaxWithHolding);
              }}
              testId='state-tax'
              max={100}
              min={0}
              error={stateTaxError && stateTaxConfig.inlineErrorMessage}
              errorText={
                stateTaxError && stateTaxConfig.inlineErrorMessage
                  ? stateTaxConfig.inlineErrorMessage(
                      applicableData?.federalTaxPercentage,
                      isWithdrawAll,
                    )
                  : ''
              }
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <WithdrawalBreakdown
            accountId={applicableData.subAccount?.account?.id}
            accountType={applicableData?.subAccount?.account?.type}
            availableToWithdrawCents={availableCents}
            withdrawAmountCents={applicableData.amountCents}
            isGrossPending={withdrawalMode.grossPending}
            withdrawalMode={withdrawalMode.mode}
            netAmountCents={applicableData.netAmountCents}
            onNetFeesChange={withdrawalMode.onNetFeesChange}
            federalTaxWithheld={
              usTransferFlowEnabled && federalTaxWithheld > 0
                ? federalTaxWithheld
                : undefined
            }
            stateTaxWithheld={
              usTransferFlowEnabled && stateTaxWithheld > 0
                ? stateTaxWithheld
                : undefined
            }
            isMultiCurrencyAccount={
              enableMultiCurrencyWithdrawalTransfers &&
              applicableData?.subAccount?.account?.availableCurrencies?.length >
                1
            }
            bankAccountCurrency={applicableData?.bankAccount?.currency}
            multiCurrencyAmounts={
              applicableData?.subAccount?.statistics?.originalCurrencies ?? []
            }
            organizationId={activeOrganization?.id}
          />
          {IS_RRSP &&
            applicableData.withdrawalReason ===
              WithdrawalReason.LifelongLearningPlan &&
            !hasOverrideTransferPermission && (
              <TransferErrorBanner
                theme={theme}
                i18nKey='transfer:withdrawal.withdrawRrspAccountLlpPlanError'
                components={[
                  <ExternalLink
                    key='0'
                    href='https://www.canada.ca/content/dam/cra-arc/formspubs/pbg/rc96/rc96-22e.pdf'
                    style={{ color: hyperLinkColor }}
                  />,
                  <ExternalLink
                    key='1'
                    href={supportUrl}
                    style={{ color: hyperLinkColor }}
                  />,
                ]}
              />
            )}
          {IS_RRSP &&
            applicableData.withdrawalReason ===
              WithdrawalReason.HomeBuyersPlan &&
            !hasOverrideTransferPermission && (
              <TransferErrorBanner
                theme={theme}
                i18nKey='transfer:withdrawal.withdrawRrspAccountHbpPlanError'
                components={[
                  <ExternalLink
                    key='0'
                    href='https://www.canada.ca/content/dam/cra-arc/formspubs/pbg/t1036/t1036-24e.pdf'
                    style={{ color: hyperLinkColor }}
                  />,
                  <ExternalLink
                    key='1'
                    href={supportUrl}
                    style={{ color: hyperLinkColor }}
                  />,
                ]}
              />
            )}
          {IS_RESP && (
            <TransferErrorBanner
              theme={theme}
              i18nKey='transfer:withdrawal.withdrawRespAccountError'
              components={[
                <ExternalLink
                  key='0'
                  href='https://www.cifinancial.com/content/dam/ci-investment-services/forms/202309/CI IS- Registered Accounts RESP Withdrawal Form -E_202308.pdf'
                  style={{ color: hyperLinkColor }}
                />,
                <ExternalLink
                  key='1'
                  href={supportUrl}
                  style={{ color: hyperLinkColor }}
                />,
              ]}
            />
          )}
          {IS_LOCKED_IN && (
            <TransferErrorBanner
              theme={theme}
              i18nKey='transfer:withdrawal.withdrawLockedInAccountError'
              supportUrl={supportUrl}
              linkSx={{ color: hyperLinkColor }}
            />
          )}
          {IS_FHSA && (
            <TransferErrorBanner
              theme={theme}
              i18nKey='transfer:withdrawal.withdrawFhsaAccountError'
              supportUrl={supportUrl}
              linkSx={{ color: hyperLinkColor }}
            />
          )}
          {IS_INCOME_FUND_ACCOUNT && (
            <TransferErrorBanner
              theme={theme}
              values={{
                accountType: t(
                  `accountTypes:${applicableData.subAccount?.account?.type}`,
                ),
              }}
              i18nKey='transfer:withdrawal.withdrawIncomeFundsAccountError'
              supportUrl={supportUrl}
              linkSx={{ color: hyperLinkColor }}
            />
          )}
          {federalTaxConfig && stateTaxConfig && IS_USA_RT_IRA && (
            <Alert sx={{ mt: 2 }} severity='info' data-testid='tax-message'>
              <div
                dangerouslySetInnerHTML={{
                  __html: t('transfer:withdrawal.rothIraTaxDisclaimer'),
                }}
              />
            </Alert>
          )}
          {isPrematureWithdrawal && (
            <Alert
              sx={{ mt: 2 }}
              severity='info'
              data-testid='premature-withdrawal-message'
            >
              {t('transfer:withdrawal.prematureWithdrawalMessage')}
            </Alert>
          )}
          {federalTaxConfig &&
            stateTaxConfig &&
            stateTaxConfig.stateTaxInfoMessage &&
            stateTaxConfig.stateTaxInfoMessage(
              applicableData?.federalTaxPercentage,
            ) && (
              <Alert sx={{ mt: 2 }} severity='info' data-testid='tax-message'>
                {stateTaxConfig.stateTaxInfoMessage(
                  applicableData?.federalTaxPercentage,
                )}
              </Alert>
            )}
        </Grid>
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default WithdrawForm;
